import React, { useState } from 'react';
import { X, Copy, CheckCircle, Code, Link, Phone, Palette } from 'lucide-react';
import Toast from './Toast';
import VoiceWidgetThemeCustomizer from './VoiceWidgetThemeCustomizer';

const COPY_LABELS = {
  url: 'URL',
  fullIframe: 'Full-page iframe',
  inlineIframe: 'Inline card iframe',
  floatingScript: 'Floating button script',
};

const CopyButton = ({ text, type, copySuccess, onCopy }) => (
  <button
    onClick={() => onCopy(text, type)}
    className="px-3 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg transition-colors flex items-center gap-1.5 self-start text-sm flex-shrink-0"
  >
    {copySuccess[type] ? (
      <><CheckCircle className="w-3.5 h-3.5" />Copied</>
    ) : (
      <><Copy className="w-3.5 h-3.5" />Copy</>
    )}
  </button>
);

const CodeBlock = ({ value, rows = 3 }) => (
  <div className="flex-1 p-3 bg-muted/50 border border-border rounded-lg font-mono text-xs text-foreground overflow-x-auto max-h-40 overflow-y-auto">
    <pre className="whitespace-pre-wrap break-words">{value}</pre>
  </div>
);

const VoiceWidgetEmbedModal = ({ agent, onClose }) => {
  const [copySuccess, setCopySuccess] = useState({
    url: false, fullIframe: false, inlineIframe: false, floatingScript: false,
  });
  const [toast, setToast] = useState(null);
  const [showCustomizer, setShowCustomizer] = useState(false);
  const [agentState, setAgentState] = useState(agent);

  const baseUrl = window.location.origin;
  const widgetBase = `${baseUrl}/embed/voice-widget/${agentState.id}`;

  const fullIframeCode =
    `<iframe\n  src="${widgetBase}?view=full"\n  width="100%"\n  height="700"\n  frameborder="0"\n  allow="microphone"\n  style="border-radius:16px; box-shadow:0 12px 36px rgba(15,23,42,0.15);"\n></iframe>`;

  const inlineIframeCode =
    `<iframe\n  src="${widgetBase}?view=inline"\n  width="100%"\n  height="480"\n  frameborder="0"\n  allow="microphone"\n  style="border-radius:16px; box-shadow:0 8px 24px rgba(15,23,42,0.12);"\n></iframe>`;

  const floatingScriptCode =
    `<script>\n(function() {\n  var i = document.createElement('iframe');\n  i.src = '${widgetBase}?view=button';\n  i.style.cssText = 'position:fixed;bottom:0;right:0;width:400px;height:680px;border:none;z-index:999999;background:transparent;';\n  i.setAttribute('allow', 'microphone');\n  document.body.appendChild(i);\n})();\n</script>`;

  const handleCopy = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(prev => ({ ...prev, [type]: true }));
      setToast({ message: `${COPY_LABELS[type]} copied!`, type: 'success' });
      setTimeout(() => setCopySuccess(prev => ({ ...prev, [type]: false })), 2000);
    } catch {
      setToast({ message: 'Failed to copy', type: 'error' });
    }
  };

  const openPreview = (view) => window.open(`${widgetBase}?view=${view}`, '_blank');

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-elegant">

        {/* Header */}
        <div className="bg-gradient-secondary text-white p-5 flex justify-between items-center rounded-t-2xl flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center">
              <Phone className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-lg font-semibold tracking-tight">Embed Voice Widget: {agentState.name}</h2>
              <p className="text-white/70 text-xs mt-0.5 font-normal">Get embed code for your customizable voice call widget</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-colors" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-7">

          {/* How it works info */}
          <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 text-sm text-foreground">
            <p className="font-semibold text-primary mb-1">How it works</p>
            <p className="text-muted-foreground text-xs leading-relaxed">
              The voice widget connects visitors to your AI agent over WebRTC — no phone number needed.
              Choose a snippet below and paste it into any HTML page. The widget respects the theme and
              customization you configure in the "Customize Widget" settings.
            </p>
          </div>

          {/* Direct URL */}
          <div className="space-y-2.5">
            <div className="flex items-center gap-2">
              <Link className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-semibold text-foreground">Direct URL</h3>
            </div>
            <div className="flex gap-2">
              <div className="flex-1 p-3 bg-muted/50 border border-border rounded-lg font-mono text-sm text-foreground break-all">
                {widgetBase}
              </div>
              <CopyButton text={widgetBase} type="url" copySuccess={copySuccess} onCopy={handleCopy} />
            </div>
            <p className="text-xs text-muted-foreground">Link visitors directly to a full-page voice call interface.</p>
          </div>

          {/* Full-page iframe */}
          <div className="space-y-2.5">
            <div className="flex items-center gap-2">
              <Code className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-semibold text-foreground">Full-Page Embed</h3>
            </div>
            <div className="flex gap-2">
              <CodeBlock value={fullIframeCode} />
              <CopyButton text={fullIframeCode} type="fullIframe" copySuccess={copySuccess} onCopy={handleCopy} />
            </div>
            <p className="text-xs text-muted-foreground">
              Drop this iframe into a dedicated page section. Recommended height: 700px.
            </p>
          </div>

          {/* Inline card */}
          <div className="space-y-2.5">
            <div className="flex items-center gap-2">
              <Code className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-semibold text-foreground">Inline Card Embed</h3>
            </div>
            <div className="flex gap-2">
              <CodeBlock value={inlineIframeCode} />
              <CopyButton text={inlineIframeCode} type="inlineIframe" copySuccess={copySuccess} onCopy={handleCopy} />
            </div>
            <p className="text-xs text-muted-foreground">
              A compact call card that fits inside any page layout. Recommended height: 480px.
            </p>
          </div>

          {/* Floating button (JavaScript snippet) */}
          <div className="space-y-2.5">
            <div className="flex items-center gap-2">
              <Code className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-semibold text-foreground">Floating Button</h3>
              <span className="ml-auto text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">Recommended</span>
            </div>
            <div className="flex gap-2">
              <CodeBlock value={floatingScriptCode} />
              <CopyButton text={floatingScriptCode} type="floatingScript" copySuccess={copySuccess} onCopy={handleCopy} />
            </div>
            <p className="text-xs text-muted-foreground">
              Paste before <code className="font-mono bg-muted px-1 rounded">&lt;/body&gt;</code> to inject a
              floating call button in the corner of any page. The button position, color, and icon are
              controlled by your widget theme settings.
            </p>
          </div>

          {/* Preview row */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground">Preview Modes</h3>
            <div className="grid grid-cols-3 gap-3">
              {[
                { view: 'button', label: 'Floating Button', desc: 'See the launcher + expanded card' },
                { view: 'inline', label: 'Inline Card',     desc: 'Compact card for page sections' },
                { view: 'full',   label: 'Full Page',       desc: 'Standalone call interface' },
              ].map(({ view, label, desc }) => (
                <button
                  key={view}
                  onClick={() => openPreview(view)}
                  className="flex flex-col items-start gap-1 p-3 bg-muted/30 hover:bg-muted/60 border border-border rounded-xl text-left transition-colors"
                >
                  <span className="text-sm font-semibold text-foreground">{label}</span>
                  <span className="text-xs text-muted-foreground">{desc}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-border p-4 bg-muted/30 flex justify-between items-center flex-shrink-0">
          <button
            onClick={() => setShowCustomizer(true)}
            className="px-4 py-2 bg-gradient-button hover:opacity-90 text-primary-foreground rounded-lg transition-all flex items-center gap-2 text-sm"
          >
            <Palette className="w-4 h-4" />
            Customize Widget
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-muted hover:bg-muted/80 text-foreground rounded-lg transition-colors text-sm"
          >
            Close
          </button>
        </div>
      </div>

      {/* Theme customizer sub-modal */}
      {showCustomizer && (
        <VoiceWidgetThemeCustomizer
          agent={agentState}
          onClose={() => setShowCustomizer(false)}
          onSave={(newSettings) => {
            setAgentState(prev => ({ ...prev, ...newSettings }));
            setShowCustomizer(false);
          }}
        />
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default VoiceWidgetEmbedModal;
